import {
  campaignApiDelete,
  campaignApiGet,
  campaignApiPatch,
  campaignApiPost,
  campaignApiPut,
} from '@/lib/api/campaign-api-client';
import type {
  AudienceEstimateResponseApi,
  AudienceEstimateResponseEntityResponseApi,
  AudienceSkipCountsApi,
  CampaignApi,
  CampaignApiEnvelope,
  CancelScheduleEntityResponseApi,
  CancelScheduleResponseApi,
  CampaignContentVersionEntityResponseApi,
  CampaignDetailResponseEntityResponseApi,
  CampaignSummaryPagedResponseApi,
  CampaignSummaryPagedResponseEntityResponseApi,
  PreSendCheckApi,
  ScheduleCampaignRequestApi,
  SendEligibilityResponseApi,
  SendEligibilityResponseEntityResponseApi,
  SendJobProgressApi,
  SendJobProgressEntityResponseApi,
  SendJobResponseApi,
  SendJobResponseEntityResponseApi,
  TestSendEntityResponseApi,
  TestSendResponseApi,
} from '../types/campaigns-api.types';
import type {
  AudienceEstimate,
  AudienceSkipCounts,
  ApplyTemplateInput,
  CancelScheduleResult,
  CampaignContentVersion,
  CampaignRestDetail,
  CampaignRestListResult,
  CreateCampaignRestInput,
  DuplicateCampaignRestInput,
  GetCampaignsRestParams,
  PreSendCheck,
  PutCampaignContentInput,
  ScheduleCampaignRestInput,
  SendCampaignRestInput,
  SendEligibility,
  SendJobProgress,
  SendJobResult,
  SetCampaignAudienceInput,
  TestSendInput,
  TestSendResult,
  UpdateCampaignRestInput,
} from '../types/campaigns-rest.types';
import {
  mapCampaignApiToRestDetail,
  mapCampaignContentVersionApi,
  mapCampaignDetailResponse,
  mapCampaignSummaryApi,
  toCreateCampaignRequestApi,
  toSetCampaignAudienceRequestApi,
  toUpdateCampaignRequestApi,
} from '../utils/campaign-api-mapper';

const assertCampaignApiSuccess = <T>(
  response: CampaignApiEnvelope<T> | null | undefined,
  errorLabel: string
): T => {
  if (!response || typeof response !== 'object') {
    throw new Error(`${errorLabel}: invalid response structure`);
  }

  if (response.success === false) {
    const message =
      response.message ||
      (Array.isArray(response.errors) ? response.errors.join(', ') : undefined) ||
      errorLabel;
    throw new Error(message);
  }

  const data = response.data;
  if (data === undefined || data === null) {
    throw new Error(`${errorLabel}: missing data payload`);
  }

  return data;
};

const requireCampaignDetail = (
  detail: CampaignRestDetail | null,
  errorLabel: string
): CampaignRestDetail => {
  if (!detail) {
    throw new Error(`${errorLabel}: campaign not found`);
  }

  return detail;
};

const mapSkipCounts = (skips: AudienceSkipCountsApi | null | undefined): AudienceSkipCounts => ({
  unsubscribed: skips?.unsubscribed ?? 0,
  pending: skips?.pending ?? 0,
  suppressed: skips?.suppressed ?? 0,
  noAddress: skips?.noAddress ?? 0,
  invalidEmail: skips?.invalidEmail ?? 0,
  noSubscriptionStatus: skips?.noSubscriptionStatus ?? 0,
});

const mapPreSendCheck = (item: PreSendCheckApi): PreSendCheck => ({
  code: item.code?.trim() || '',
  message: item.message?.trim() || item.code?.trim() || '',
  severity: item.severity?.trim().toLowerCase() || 'error',
});

const mapSendEligibility = (data: SendEligibilityResponseApi): SendEligibility => {
  const blocked = (data.blocked ?? []).map(mapPreSendCheck).filter((item) => item.message);
  return {
    sendable: data.sendable,
    blocked,
    blockedMessages: blocked.map((item) => item.message),
    recipients: data.recipients
      ? {
          include: data.recipients.include ?? 0,
          duplicatesMerged: data.recipients.duplicatesMerged ?? 0,
          exclude: data.recipients.exclude ?? 0,
          removedByExclusion: data.recipients.removedByExclusion ?? 0,
          net: data.recipients.net ?? 0,
          sendable: data.recipients.sendable ?? 0,
          skips: mapSkipCounts(data.recipients.skips),
        }
      : null,
  };
};

const mapAudienceEstimate = (data: AudienceEstimateResponseApi): AudienceEstimate => ({
  includeCount: data.includeCount ?? 0,
  duplicatesMerged: data.duplicatesMerged ?? 0,
  excludeCount: data.excludeCount ?? 0,
  removedByExclusion: data.removedByExclusion ?? 0,
  netCount: data.netCount ?? 0,
  sendableCount: data.sendableCount ?? 0,
  skips: mapSkipCounts(data.skips),
  truncated: Boolean(data.truncated),
  evaluatedAt: data.evaluatedAt ?? null,
});

const mapSendJob = (data: SendJobResponseApi, fallbackCampaignId: string): SendJobResult => ({
  jobId: data.itemId?.trim() || '',
  campaignId: data.campaignId?.trim() || fallbackCampaignId,
  mode: data.mode?.trim() || 'immediate',
  status: data.status?.trim() || 'queued',
  recipientCount: data.recipientCount ?? 0,
  scheduledAt: data.scheduledAt?.trim() || null,
});

export const getCampaignsRest = async (
  params: GetCampaignsRestParams = {}
): Promise<CampaignRestListResult> => {
  const pageNo = params.pageNo ?? 1;
  const pageSize = params.pageSize ?? 100;
  const offset = Math.max(0, (pageNo - 1) * pageSize);

  const query: Record<string, string | number | string[] | undefined> = {
    limit: pageSize,
    offset,
  };

  const search = params.q?.trim();
  if (search) {
    query.q = search;
  }

  const status = params.status?.map((value) => value.trim().toLowerCase()).filter(Boolean);
  if (status?.length) {
    query.status = status;
  }

  const sort = params.sort?.trim();
  if (sort) {
    query.sort = sort;
  }

  const order = params.order?.trim();
  if (order) {
    query.order = order;
  }

  if (params.channel) {
    query.channel = params.channel;
  }

  const response = await campaignApiGet<CampaignSummaryPagedResponseEntityResponseApi>(
    '/v1/campaigns',
    query
  );

  const data = assertCampaignApiSuccess<CampaignSummaryPagedResponseApi>(
    response,
    'Failed to fetch campaigns'
  );

  const items = (data.items ?? [])
    .map(mapCampaignSummaryApi)
    .filter((item): item is NonNullable<typeof item> => item !== null);

  const limit = data.pageInfo?.limit ?? pageSize;
  const responseOffset = data.pageInfo?.offset ?? offset;
  const totalCount = data.pageInfo?.total ?? items.length;
  const responsePageNo = limit > 0 ? Math.floor(responseOffset / limit) + 1 : pageNo;
  const totalPages = limit > 0 ? Math.max(1, Math.ceil(totalCount / limit)) : 1;
  const hasNextPage =
    typeof data.pageInfo?.hasNext === 'boolean'
      ? data.pageInfo.hasNext
      : responseOffset + items.length < totalCount;
  const hasPreviousPage =
    typeof data.pageInfo?.hasPrev === 'boolean' ? data.pageInfo.hasPrev : responseOffset > 0;

  return {
    items,
    totalCount,
    pageNo: responsePageNo,
    pageSize: limit,
    totalPages,
    hasNextPage,
    hasPreviousPage,
  };
};

export const getCampaignRestById = async (campaignId: string): Promise<CampaignRestDetail> => {
  const id = campaignId.trim();
  if (!id) {
    throw new Error('Campaign id is required');
  }

  const response = await campaignApiGet<CampaignDetailResponseEntityResponseApi>(
    `/v1/campaigns/${encodeURIComponent(id)}`
  );
  const data = assertCampaignApiSuccess(response, 'Failed to fetch campaign');
  const detail = mapCampaignDetailResponse(data);

  return requireCampaignDetail(detail, 'Failed to fetch campaign');
};

export const createCampaignRest = async (
  input: CreateCampaignRestInput
): Promise<CampaignRestDetail> => {
  const body = toCreateCampaignRequestApi(input);
  if (!body.name) {
    throw new Error('Name is required');
  }

  const response = await campaignApiPost<CampaignApiEnvelope<CampaignApi>>('/v1/campaigns', body);
  const data = assertCampaignApiSuccess(response, 'Failed to create campaign');
  const detail = mapCampaignApiToRestDetail(data);

  return requireCampaignDetail(detail, 'Failed to create campaign');
};

export const updateCampaignRest = async (
  campaignId: string,
  input: UpdateCampaignRestInput
): Promise<CampaignRestDetail> => {
  const id = campaignId.trim();
  if (!id) {
    throw new Error('Campaign id is required');
  }

  const body = toUpdateCampaignRequestApi(input);
  const response = await campaignApiPatch<CampaignApiEnvelope<CampaignApi>>(
    `/v1/campaigns/${encodeURIComponent(id)}`,
    body
  );
  const data = assertCampaignApiSuccess(response, 'Failed to update campaign');
  const detail = mapCampaignApiToRestDetail(data);

  return requireCampaignDetail(detail, 'Failed to update campaign');
};

export const setCampaignAudienceRest = async (
  campaignId: string,
  input: SetCampaignAudienceInput
): Promise<CampaignRestDetail> => {
  const id = campaignId.trim();
  if (!id) {
    throw new Error('Campaign id is required');
  }

  const body = toSetCampaignAudienceRequestApi(input);
  const response = await campaignApiPut<CampaignApiEnvelope<CampaignApi>>(
    `/v1/campaigns/${encodeURIComponent(id)}/audience`,
    body
  );
  const data = assertCampaignApiSuccess(response, 'Failed to set campaign audience');
  const detail = mapCampaignApiToRestDetail(data);

  return requireCampaignDetail(detail, 'Failed to set campaign audience');
};

export const getCampaignAudienceEstimate = async (
  campaignId: string
): Promise<AudienceEstimate> => {
  const id = campaignId.trim();
  if (!id) {
    throw new Error('Campaign id is required');
  }

  const response = await campaignApiGet<AudienceEstimateResponseEntityResponseApi>(
    `/v1/campaigns/${encodeURIComponent(id)}/audience/estimate`
  );
  const data = assertCampaignApiSuccess(response, 'Failed to estimate audience');
  return mapAudienceEstimate(data);
};

export const getCampaignSendEligibility = async (campaignId: string): Promise<SendEligibility> => {
  const id = campaignId.trim();
  if (!id) {
    throw new Error('Campaign id is required');
  }

  const response = await campaignApiGet<SendEligibilityResponseEntityResponseApi>(
    `/v1/campaigns/${encodeURIComponent(id)}/send-eligibility`
  );
  const data = assertCampaignApiSuccess(response, 'Failed to fetch send eligibility');

  return mapSendEligibility(data);
};

export const duplicateCampaignRest = async (
  campaignId: string,
  input: DuplicateCampaignRestInput
): Promise<CampaignRestDetail> => {
  const id = campaignId.trim();
  const name = input.name.trim();
  if (!id) {
    throw new Error('Campaign id is required');
  }
  if (!name) {
    throw new Error('Name is required for the duplicated campaign');
  }

  const response = await campaignApiPost<CampaignApiEnvelope<CampaignApi>>(
    `/v1/campaigns/${encodeURIComponent(id)}/duplicate`,
    { name }
  );
  const data = assertCampaignApiSuccess(response, 'Failed to duplicate campaign');
  const detail = mapCampaignApiToRestDetail(data);

  return requireCampaignDetail(detail, 'Failed to duplicate campaign');
};

export const deleteCampaignRest = async (campaignId: string): Promise<void> => {
  const id = campaignId.trim();
  if (!id) {
    throw new Error('Campaign id is required');
  }

  const response = await campaignApiDelete<CampaignApiEnvelope<boolean>>(
    `/v1/campaigns/${encodeURIComponent(id)}`
  );
  assertCampaignApiSuccess(response, 'Failed to delete campaign');
};

/** Guide §11 — POST /v1/campaigns/{itemId}/send with Idempotency-Key. */
export const sendCampaignRest = async (input: SendCampaignRestInput): Promise<SendJobResult> => {
  const id = input.campaignId.trim();
  const idempotencyKey = input.idempotencyKey.trim();
  if (!id) {
    throw new Error('Campaign id is required');
  }
  if (!idempotencyKey) {
    throw new Error("The 'Idempotency-Key' header is required for this endpoint.");
  }
  if (idempotencyKey.length > 255) {
    throw new Error("The 'Idempotency-Key' header must be at most 255 characters.");
  }

  const body =
    typeof input.confirmEstimatedRecipients === 'number'
      ? { confirmEstimatedRecipients: input.confirmEstimatedRecipients }
      : {};

  const response = await campaignApiPost<SendJobResponseEntityResponseApi>(
    `/v1/campaigns/${encodeURIComponent(id)}/send`,
    body,
    { 'Idempotency-Key': idempotencyKey }
  );
  const data = assertCampaignApiSuccess(response, 'Failed to send campaign');
  return mapSendJob(data, id);
};

/** Guide §5 — POST /v1/campaigns/{itemId}/schedule with Idempotency-Key. */
export const scheduleCampaignRest = async (
  input: ScheduleCampaignRestInput
): Promise<SendJobResult> => {
  const id = input.campaignId.trim();
  const idempotencyKey = input.idempotencyKey.trim();
  if (!id) {
    throw new Error('Campaign id is required');
  }
  if (!idempotencyKey) {
    throw new Error("The 'Idempotency-Key' header is required for this endpoint.");
  }
  if (idempotencyKey.length > 255) {
    throw new Error("The 'Idempotency-Key' header must be at most 255 characters.");
  }
  if (input.mode !== 'scheduled' && input.mode !== 'recurring') {
    throw new Error('Schedule mode must be "scheduled" or "recurring".');
  }

  const body: ScheduleCampaignRequestApi = {
    mode: input.mode,
  };

  if (input.mode === 'scheduled') {
    const scheduledAt = input.scheduledAt?.trim();
    if (!scheduledAt) {
      throw new Error('scheduledAt is required for a one-off schedule.');
    }
    body.scheduledAt = scheduledAt;
    if (input.timeZone?.trim()) {
      body.timeZone = input.timeZone.trim();
    }
  } else {
    if (!input.recurrence?.frequency) {
      throw new Error('recurrence.frequency is required for a recurring schedule.');
    }
    body.recurrence = {
      frequency: input.recurrence.frequency,
      interval: input.recurrence.interval ?? 1,
      byWeekday: input.recurrence.byWeekday ?? undefined,
      byMonthDay: input.recurrence.byMonthDay ?? undefined,
      startDate: input.recurrence.startDate ?? undefined,
      endDate: input.recurrence.endDate ?? undefined,
      occurrences: input.recurrence.occurrences ?? undefined,
      timeOfDay: input.recurrence.timeOfDay ?? undefined,
      timeZone: input.recurrence.timeZone ?? undefined,
    };
  }

  if (typeof input.throttlePerHour === 'number') {
    body.throttlePerHour = input.throttlePerHour;
  }

  const response = await campaignApiPost<SendJobResponseEntityResponseApi>(
    `/v1/campaigns/${encodeURIComponent(id)}/schedule`,
    body,
    { 'Idempotency-Key': idempotencyKey }
  );
  const data = assertCampaignApiSuccess(response, 'Failed to schedule campaign');
  return mapSendJob(data, id);
};

/** Guide §6 — DELETE /v1/campaigns/{itemId}/schedule. */
export const cancelCampaignScheduleRest = async (
  campaignId: string
): Promise<CancelScheduleResult> => {
  const id = campaignId.trim();
  if (!id) {
    throw new Error('Campaign id is required');
  }

  const response = await campaignApiDelete<CancelScheduleEntityResponseApi>(
    `/v1/campaigns/${encodeURIComponent(id)}/schedule`
  );
  const data = assertCampaignApiSuccess<CancelScheduleResponseApi>(
    response,
    'Failed to cancel schedule'
  );
  return {
    campaignId: data.campaignId?.trim() || id,
    cancelledJobIds: (data.cancelledJobIds ?? []).map((jobId) => jobId.trim()).filter(Boolean),
    campaignStatus: data.campaignStatus?.trim() || 'draft',
  };
};

export const getCampaignContentRest = async (
  campaignId: string,
  version?: number
): Promise<CampaignContentVersion> => {
  const id = campaignId.trim();
  if (!id) {
    throw new Error('Campaign id is required');
  }

  const response = await campaignApiGet<CampaignContentVersionEntityResponseApi>(
    `/v1/campaigns/${encodeURIComponent(id)}/content`,
    typeof version === 'number' ? { version } : {}
  );
  const data = assertCampaignApiSuccess(response, 'Failed to fetch campaign content');
  return mapCampaignContentVersionApi(data);
};

export const putCampaignContentRest = async (
  campaignId: string,
  input: PutCampaignContentInput
): Promise<CampaignContentVersion> => {
  const id = campaignId.trim();
  if (!id) {
    throw new Error('Campaign id is required');
  }
  const html = input.html.trim();
  if (!html) {
    throw new Error('html is required');
  }

  const body = {
    html,
    plainText: input.plainText === undefined ? null : input.plainText,
  };

  const response = await campaignApiPut<CampaignContentVersionEntityResponseApi>(
    `/v1/campaigns/${encodeURIComponent(id)}/content`,
    body
  );
  const data = assertCampaignApiSuccess(response, 'Failed to save campaign content');
  return mapCampaignContentVersionApi(data);
};

export const applyCampaignTemplateRest = async (
  campaignId: string,
  input: ApplyTemplateInput
): Promise<CampaignContentVersion> => {
  const id = campaignId.trim();
  const templateId = input.templateId.trim();
  if (!id) {
    throw new Error('Campaign id is required');
  }
  if (!templateId) {
    throw new Error('templateId is required');
  }

  const response = await campaignApiPost<CampaignContentVersionEntityResponseApi>(
    `/v1/campaigns/${encodeURIComponent(id)}/apply-template`,
    { templateId }
  );
  const data = assertCampaignApiSuccess(response, 'Failed to apply template');
  return mapCampaignContentVersionApi(data);
};

export const testSendCampaignRest = async (input: TestSendInput): Promise<TestSendResult> => {
  const id = input.campaignId.trim();
  const idempotencyKey = input.idempotencyKey.trim();
  const recipients = input.recipients.map((value) => value.trim().toLowerCase()).filter(Boolean);

  if (!id) {
    throw new Error('Campaign id is required');
  }
  if (!idempotencyKey) {
    throw new Error("The 'Idempotency-Key' header is required for this endpoint.");
  }
  if (recipients.length < 1 || recipients.length > 5) {
    throw new Error('Test send accepts between 1 and 5 recipients.');
  }

  const body: { recipients: string[]; contactId?: string } = { recipients };
  const contactId = input.contactId?.trim();
  if (contactId) {
    body.contactId = contactId;
  }

  const response = await campaignApiPost<TestSendEntityResponseApi>(
    `/v1/campaigns/${encodeURIComponent(id)}/test-send`,
    body,
    { 'Idempotency-Key': idempotencyKey }
  );
  const data = assertCampaignApiSuccess<TestSendResponseApi>(response, 'Failed to send test');
  return {
    accepted: data.accepted ?? recipients.length,
    recipients: Array.isArray(data.recipients) ? data.recipients.filter(Boolean) : recipients,
  };
};

const mapSendJobProgress = (data: SendJobProgressApi): SendJobProgress => {
  const throughput =
    typeof data.throughputPerMinute === 'number' && data.throughputPerMinute > 0
      ? data.throughputPerMinute
      : null;

  return {
    status: data.status?.trim() || 'queued',
    recipientCount: data.recipientCount ?? 0,
    processedCount: data.processedCount ?? 0,
    sentCount: data.sentCount ?? 0,
    failedCount: data.failedCount ?? 0,
    skippedCount: data.skippedCount ?? 0,
    skipReasons: (data.skipReasons ?? [])
      .map((item) => ({
        reason: item.reason?.trim() || '',
        count: item.count ?? 0,
      }))
      .filter((item) => item.reason),
    throughputPerMinute: throughput,
    startedAt: data.startedAt ?? null,
    estimatedCompletionAt: data.estimatedCompletionAt ?? null,
    lastError: data.lastError ?? null,
  };
};

export const getSendJobProgressRest = async (jobId: string): Promise<SendJobProgress> => {
  const id = jobId.trim();
  if (!id) {
    throw new Error('Send job id is required');
  }

  const response = await campaignApiGet<SendJobProgressEntityResponseApi>(
    `/v1/send-jobs/${encodeURIComponent(id)}/progress`
  );
  const data = assertCampaignApiSuccess(response, 'Failed to fetch send job progress');
  return mapSendJobProgress(data);
};
